import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Profile() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="profile" ref={ref} className="relative py-24 md:py-32 overflow-hidden">
      {/* Silk texture background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{
          backgroundImage: 'url(https://readdy.ai/api/search-image?query=Soft%20elegant%20silk%20fabric%20texture%2C%20gentle%20pink%20and%20cream%20fabric%20folds%2C%20warm%20ivory%20satin%20draping%2C%20luxurious%20refined%20backdrop%2C%20subtle%20sheen%20and%20soft%20diffused%20lighting%2C%20high-end%20fashion%20editorial%20style%2C%20delicate%20fabric%20waves%2C%20seamless%20silk%20background&width=1200&height=800&seq=profile-silk-bg&orientation=landscape)',
        }}
      ></div>
      <div className="absolute inset-0 bg-warm-ivory/70"></div>
      <div className="absolute top-40 -left-32 w-[400px] h-[400px] bg-champagne-200/20 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-20 -right-24 w-[350px] h-[350px] bg-gold-300/5 rounded-full blur-[110px]"></div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-8">
        {/* Header */}
        <div className={`mb-16 md:mb-20 ${isVisible ? 'reveal revealed' : 'reveal'}`}>
          <p className="text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-4 font-medium">プロフィール</p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-charcoal-800 tracking-tight">
            PROFILE
          </h2>
        </div>

        {/* Name — above photo + biography */}
        <div className={`text-center mb-12 ${isVisible ? 'reveal revealed' : 'reveal'} reveal-delay-1`}>
          <p className="font-heading text-2xl md:text-3xl font-semibold text-charcoal-800 tracking-tight mb-1">
            AMERI
          </p>
          <p className="font-heading text-lg text-charcoal-500">アメリ</p>
        </div>

        {/* Photo + Biography side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {/* Left: Photo + stats + SNS */}
          <div className={`lg:col-span-4 flex flex-col items-center lg:items-start gap-6 ${isVisible ? 'reveal revealed' : 'reveal'} reveal-delay-2`}>
            {/* Photo */}
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-champagne-200/50 via-warm-ivory/20 to-gold-200/20 rounded-full blur-2xl"></div>
              <div className="relative w-64 h-64 md:w-72 md:h-72 rounded-full overflow-hidden ring-1 ring-gold-300/50 shadow-[0_8px_32px_rgba(139,90,43,0.12)] hover:shadow-[0_12px_40px_rgba(139,90,43,0.18)] transition-shadow duration-500">
                <img
                  alt="アメリ"
                  className="w-full h-full object-cover object-top"
                  src="https://storage.readdy-site.link/project_files/346f2ee0-d729-4568-8b32-aa7af4cc60d4/22ecbaa5-ec84-4a18-bb9f-79a69d1f1d2a_compressed_555802444_777730355090152_2918592743102479657_n.webp"
                />
              </div>
              {/* Portrait credit */}
              <p className="text-[10px] text-charcoal-800 text-center mt-3 tracking-wide font-light">Portrait Illustration by @jikuu24</p>
            </div>

            {/* Stat badges */}
            <div className="flex flex-wrap gap-5">
              <div className="relative bg-gradient-to-br from-white/95 to-champagne-50/80 ring-1 ring-gold-300/50 px-5 py-3 shadow-[0_4px_24px_rgba(139,90,43,0.06)] overflow-hidden hover:shadow-[0_6px_28px_rgba(139,90,43,0.1)] transition-shadow duration-300">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent"></div>
                <p className="text-2xl md:text-3xl font-bold text-gold-500 leading-none" style={{ fontFamily: 'var(--font-heading)' }}>30+</p>
                <p className="text-[10px] text-charcoal-400 mt-1 tracking-wide">提供実績</p>
              </div>
              <div className="relative bg-gradient-to-br from-white/95 to-champagne-50/80 ring-1 ring-gold-300/50 px-5 py-3 shadow-[0_4px_24px_rgba(139,90,43,0.06)] overflow-hidden hover:shadow-[0_6px_28px_rgba(139,90,43,0.1)] transition-shadow duration-300">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent"></div>
                <p className="text-2xl md:text-3xl font-bold text-charcoal-800 leading-none" style={{ fontFamily: 'var(--font-heading)' }}>2.6億+</p>
                <p className="text-[10px] text-charcoal-400 mt-1 tracking-wide">SNS総再生数</p>
              </div>
            </div>

            {/* SNS */}
            <div className="flex items-center gap-5">
              <a
                href="https://open.spotify.com/playlist/4NIBk1d8MZ4wqHOHlNDYLF?si=00034c16fc63458e"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-charcoal-800 text-white hover:bg-gold-500 transition-all duration-300 cursor-pointer rounded-full hover:scale-110"
              >
                <i className="ri-spotify-fill text-lg"></i>
              </a>
              <a
                href="https://www.instagram.com/ameri_iria?igsh=MTRxbmZkaW5tNXRocg%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-charcoal-800 text-white hover:bg-gold-500 transition-all duration-300 cursor-pointer rounded-full hover:scale-110"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
            </div>
          </div>

          {/* Right: Biography */}
          <div className={`lg:col-span-8 flex ${isVisible ? 'reveal revealed' : 'reveal'} reveal-delay-3`}>
            <div className="relative bg-gradient-to-br from-white/90 via-champagne-50/70 to-champagne-50/20 ring-1 ring-gold-300/40 p-6 md:p-8 shadow-[0_4px_24px_rgba(139,90,43,0.06)] overflow-hidden w-full flex flex-col justify-center">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent"></div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-champagne-300"></span>
                <span className="text-[11px] tracking-[0.2em] text-charcoal-400 uppercase font-medium">経歴</span>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-charcoal-500 leading-[2] font-light">
                  福岡県出身。ブラックミュージック、ソウル、R&amp;Bの影響を受けて育ち、幼少期よりクラシックピアノを学び、音楽の基礎を培う。ボーカリストとしては、ブラックミュージック特有のグルーヴやフェイク、リズムアプローチを得意とする一方、声楽で培った発声と表現力を活かし、クラシックからポップス、R&amp;Bまで幅広いジャンルに対応する歌唱力を持つ。
                </p>
                <p className="text-sm text-charcoal-500 leading-[2] font-light">
                  高校時代には従来のポップスとは異なる音楽表現に強く惹かれ、声楽の道へ進む。声楽コンクールでは金賞を受賞し、クラシックやヨーロッパ音楽が持つ繊細な色彩感覚と表現美への理解を深めた。
                </p>
                <p className="text-sm text-charcoal-500 leading-[2] font-light">
                  その後、音楽大学へ進学。在学中より作曲・編曲・楽曲制作に携わり、制作者としてのキャリアをスタート。卒業と同時に作家活動を本格化させ、メジャーシーンにおいて国内外アーティストへの楽曲提供を開始する。
                </p>
                <p className="text-sm text-charcoal-500 leading-[2] font-light">
                  ブラックミュージック由来のグルーヴ感、クラシックで培った構築力、そして声楽によるメロディ表現を融合させた独自の音楽性を強みとし、K-POP、J-POPをはじめとする幅広いジャンルで制作を展開。国内外の多くのアーティストとの協業を重ねながら、ジャンルや国境を越えたポップミュージックの創造に取り組んでいる。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}